/**
 * Shared service worker updater.
 *
 * Dispatches a single `hibane:sw-update-available` window event when a
 * GENUINE update is ready — that is:
 *   1. There is already a controlling SW (not a first install), AND
 *   2. A newly installed SW exists (`registration.installing` reached the
 *      `installed` state, or `registration.waiting` is present at attach), AND
 *   3. The new SW's BUILD_ID differs from the active controller's BUILD_ID.
 *
 * The component (`SWUpdatePrompt`) listens for the event and decides when to
 * activate; once activated, `controllerchange` triggers a single reload.
 *
 * Registration is also restricted: never inside iframes, never on Lovable
 * preview hosts, never in dev — only on `finance.hibane.ai`.
 */

const UPDATE_EVENT = "hibane:sw-update-available";
const FINANCE_PROD_HOST = "finance.hibane.ai";

let controllerChangeBound = false;
let reloading = false;

function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function onFinanceProdHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname.toLowerCase();
  return h === FINANCE_PROD_HOST || h === `www.${FINANCE_PROD_HOST}`;
}

function getBuildId(sw: ServiceWorker | null): Promise<string | null> {
  if (!sw) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: string | null) => {
      if (done) return;
      done = true;
      resolve(v);
    };
    const timer = window.setTimeout(() => finish(null), 1500);
    try {
      const ch = new MessageChannel();
      ch.port1.onmessage = (e) => {
        window.clearTimeout(timer);
        finish((e.data && e.data.buildId) || null);
      };
      sw.postMessage({ type: "GET_BUILD_ID" }, [ch.port2]);
    } catch {
      window.clearTimeout(timer);
      finish(null);
    }
  });
}

async function maybeNotify(candidate: ServiceWorker | null) {
  if (!candidate) return;
  const active = navigator.serviceWorker.controller;
  if (!active) return; // first install — no prompt
  const [activeId, newId] = await Promise.all([
    getBuildId(active),
    getBuildId(candidate),
  ]);
  // If we got both ids and they match, this is an unchanged redeploy.
  if (activeId && newId && activeId === newId) return;
  const activate = () => {
    try {
      candidate.postMessage({ type: "SKIP_WAITING" });
    } catch {
      /* noop */
    }
  };
  window.dispatchEvent(
    new CustomEvent(UPDATE_EVENT, { detail: { activate } }),
  );
}

export function attachSwAutoUpdate(registration: ServiceWorkerRegistration) {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  // 1) Reload once when a new SW takes control
  if (!controllerChangeBound) {
    controllerChangeBound = true;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  }

  // 2) Worker already waiting at attach time
  if (registration.waiting) {
    void maybeNotify(registration.waiting);
  }

  // 3) New worker installs after attach
  registration.addEventListener("updatefound", () => {
    const nw = registration.installing;
    if (!nw) return;
    nw.addEventListener("statechange", () => {
      if (nw.state === "installed") {
        void maybeNotify(nw);
      }
    });
  });

  // 4) Periodic / focus / visibility update checks
  const check = () => {
    registration.update().catch(() => {});
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") check();
  };
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", onVisibility);
  const interval = window.setInterval(check, 60_000);

  return () => {
    window.removeEventListener("focus", check);
    document.removeEventListener("visibilitychange", onVisibility);
    window.clearInterval(interval);
  };
}

/**
 * Register a service worker and attach the auto-updater. No-ops unless we are
 * in production, on the top-level finance.hibane.ai window, and not in an
 * iframe. In all other contexts, any previously-registered SW is unregistered
 * to avoid stale caches in preview hosts.
 */
export async function registerAndAutoUpdate(
  scriptUrl: string,
  options?: RegistrationOptions,
) {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  const allowed =
    import.meta.env.PROD && !inIframe() && onFinanceProdHost();

  if (!allowed) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    } catch {
      /* noop */
    }
    return;
  }

  try {
    const reg = await navigator.serviceWorker.register(scriptUrl, {
      updateViaCache: "none",
      ...options,
    });
    attachSwAutoUpdate(reg);
    return reg;
  } catch (err) {
    console.log("SW registration failed:", scriptUrl, err);
  }
}